import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { MessageBarActions, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

// components
import { PageHeader } from '../../../commons/component/PageHeader/PageHeader';
import { HeaderPersonaComponent } from '../../../commons/component/headers/HeaderPersona/HeaderPersonaComponent';
import { withRouter } from '../../../commons/component/withRouterCustom';
import PersonResumeComponent from '../../../components/Persons/PersonForm/Person.Resume.Component';
import PersonPhonesListComponent from '../../../components/Persons/PersonPhones/PersonPhones.List.Component';
import PersonEmailsListComponent from '../../../components/Persons/PersonEmails/PersonEmails.List.Component';
import PersonAddressesListComponent from '../../../components/Persons/PersonAddresses/PersonAddresses.List.Component';

// guía
import { GuideSectionTitle } from '../../../theme/components/GuideSectionTitle';
import { PersonContactCard } from '../../../components/Persons/PersonContactList/PersonContactList.Component';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideBannerAction } from '../../../theme/components/GuideButtons';
import { StatusPill } from '../../../theme/components/StatusPill';

// functions
import * as CommonFunctions from '../../../components/Persons/Persons.Functions';
import { PersonTypeEnum } from '../../../models/Entities/Person/PersonCommands';
import * as ownFunctions from './Staff.Details.Page.Functions';

import './Staff.Details.Styles.css';

/**
 * Ficha de un miembro del staff (guía): encabezado con la persona, y solapas Datos personales,
 * Teléfonos, Correos y Domicilios. Relaciones y asociaciones externas se quitaron de la ficha;
 * sus componentes siguen en el repo por si vuelven.
 */
export const StaffDetailsPage = (props: ownFunctions.Props) => {
  const [t] = useTranslation();

  const load = () => { if (props.PersonId) props.GetPersonById(props.PersonId); };
  useEffect(() => {
    if (props.PersonId && props.Person?.id === undefined) load();
  }, [props.PersonId, props.Person?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isJuridical = props.Person?.personType === PersonTypeEnum.JuridicalPerson;

  return (
    <>
      <PageHeader title={t('staffDetailsPage.title', { defaultValue: 'Ficha del miembro del staff' })} />
      <div className="Content">
        {props.failOnLoadingPerson && (
          <div className="Section">
            <GuideMessageBar intent="error" layout="multiline" data-testid="staff-details-error">
              <MessageBarBody>
                <MessageBarTitle>{t('staffDetailsPage.error', { defaultValue: 'No fue posible obtener los datos del miembro del staff' })}</MessageBarTitle>
                {props.error?.ErrorMessage || ''}
              </MessageBarBody>
              <MessageBarActions>
                <GuideBannerAction onClick={load}>{t('staffDetailsPage.retry', { defaultValue: 'Reintentar' })}</GuideBannerAction>
              </MessageBarActions>
            </GuideMessageBar>
          </div>
        )}

        {props.isPersonLoading && !props.Person && (
          <div className="Section">
            <GuideSkeleton rows={4} label={t('staffDetailsPage.loading', { defaultValue: 'Cargando ficha...' })} data-testid="staff-details-loading" />
          </div>
        )}

        {props.Person && (
          <>
            <div className="Section sdp-header">
              <HeaderPersonaComponent
                PersonaName={CommonFunctions.GetPersonDisplayName(props.Person)}
                secondaryText={props.Person.activity || t('staffDetailsPage.subtitle', { defaultValue: 'Miembro del staff' })}
              />
              <div className="sdp-header__tags">
                <StatusPill tone="neutral" symbol="○">
                  {isJuridical ? t('persons.personType.juridical', { defaultValue: 'Persona jurídica' }) : t('persons.personType.natural', { defaultValue: 'Persona física' })}
                </StatusPill>
              </div>
            </div>
            {/* Una sola pantalla: datos personales y, debajo, las tres listas de contacto como
                secciones. Teléfonos y correos comparten fila cuando hay ancho. */}
            <div className="Section" data-testid="staff-details-personal">
              <PersonResumeComponent Person={props.Person} />
            </div>
            {/* Tarjeta de contacto compacta: teléfonos y correos lado a lado, domicilios a lo ancho. */}
            <div className="Section sdp-contact" data-testid="staff-details-contact">
              <GuideSectionTitle
                title={t('staffDetailsPage.contact.title', { defaultValue: 'Contacto' })}
                description={t('staffDetailsPage.contact.description', { defaultValue: 'Teléfonos, correos y domicilios de la persona.' })}
              />
              <PersonContactCard>
                <PersonPhonesListComponent PersonId={props.Person.id} />
                <PersonEmailsListComponent PersonId={props.Person.id} />
                <PersonAddressesListComponent PersonId={props.Person.id} />
              </PersonContactCard>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default withRouter(
  connect(ownFunctions.mapStateToProps, ownFunctions.mapDispatchToProps)(StaffDetailsPage as any)
);
